// src/pages/admin/Education/StudentRegistrations.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FaEye, 
  FaSearch, 
  FaTimes,
  FaUserGraduate,
  FaSpinner,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaUser,
  FaEnvelope,
  FaPhone,
} from 'react-icons/fa';
import { useDispatch } from "react-redux";
import { successToast, errorToast } from '../../Utils/toast';
import { openModal } from "../../redux/features/modalSlice";
import Pagination from '../../common/Pagination';
import SearchBar from '../../common/Search';
import { 
  useGetStudentRegistrationsQuery,
  useUpdateStudentStatusMutation,
} from "../../redux/services/scholarshipsService/scholarships.Service";

function StudentRegistrations() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loadingId, setLoadingId] = useState(null);

  const { data, isLoading, isError, refetch } = useGetStudentRegistrationsQuery({
    page: 1,
    limit: 20,
    status: statusFilter,
    search: searchTerm,
  });

  const [updateStudentStatus] = useUpdateStudentStatusMutation();

  const students = data?.data || [];
  const pagination = data?.pagination || {};
  const totalPages = pagination?.totalPages || 0;
  const totalItems = pagination?.total || 0;
  const currentPage = pagination?.page || 1;
  const showPagination = totalPages > 1;

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { class: 'badge bg-warning', label: 'Pending', icon: <FaClock /> },
      'approved': { class: 'badge bg-success', label: 'Approved', icon: <FaCheckCircle /> },
      'rejected': { class: 'badge bg-danger', label: 'Rejected', icon: <FaTimesCircle /> },
    };
    const statusInfo = statusMap[status] || statusMap['pending'];
    return <span className={statusInfo.class}>
      {statusInfo.icon} {statusInfo.label}
    </span>;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Handle status change
  const handleStatusChange = async (id, status) => {
    if (!window.confirm(`Are you sure you want to change status to "${status}"?`)) {
      return;
    }

    try {
      setLoadingId(id);
      await updateStudentStatus({ id, status }).unwrap();
      successToast(`Student status updated to ${status}`);
      refetch();
    } catch (error) {
      errorToast(error?.data?.message || 'Failed to update status');
    } finally {
      setLoadingId(null);
    }
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  // Handle status filter
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h5 className="mt-3">Loading Student Registrations...</h5>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-5">
        <div className="alert alert-danger">
          <h5>Something Went Wrong</h5>
          <p>Unable to load student registrations. Please try again.</p>
          <button className="btn btn-primary" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* HEADER */}
      <div className="page-header-breadcrumb all_breadcrumb">
        <div className="ms-md-1 mb-1 mb-md-0 ms-0 page_heading_breadcrumb">
          <nav>
            <h4 className="fw-semibold">
              <Link to="/admin/education" className="text-primary me-2">
                ← Back
              </Link>
              Student Registrations {totalItems > 0 && <span className="badge-custom bg-primary">{totalItems}</span>}
            </h4>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item active">
                <Link to="/admin">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/admin/education">Education</Link>
              </li>
              <li className="breadcrumb-item">Student Registrations</li>
            </ol>
          </nav>
        </div>
        <div className="page-title fw-semibold fs-18 mb-0">
          <button
            className="btn btn-primary"
            onClick={() => refetch()}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* SEARCH AND FILTER SECTION */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-center">
            <div className="col-md-4">
              <SearchBar
                search={searchTerm}
                setSearch={handleSearch}
                placeholder="Search by name, email, phone..."
              />
            </div>

            <div className="col-md-3">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="col-md-5 text-md-end">
              {(searchTerm || statusFilter) && (
                <button
                  className="btn btn-outline-secondary me-2"
                  onClick={clearFilters}
                >
                  <FaTimes className="me-1" />
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="st-wrapper-card">
        {students.length === 0 ? (
          <div className="text-center py-5">
            <div className="mb-3">
              <FaUserGraduate className="text-muted" size={48} />
            </div>
            <h5>No Student Registrations Found</h5>
            <p className="text-muted">
              {searchTerm || statusFilter
                ? "Try adjusting your search or filter criteria"
                : "No student registrations available"}
            </p>
          </div>
        ) : (
          <div className="st-table-responsive">
            <table className="table st-custom-table align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Student</th>
                  <th>Contact</th>
                  <th>Education</th>
                  <th>Course</th>
                  <th>Scholarship</th>
                  <th>Status</th>
                  <th>Registered</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {students.map((item, index) => (
                  <tr key={item.id}>
                    <td>{(currentPage - 1) * 20 + index + 1}</td>
                    <td>
                      <div className="fw-semibold">{item.full_name}</div>
                      <small className="text-muted">
                        {item.gender === 'male' ? 'Male' : item.gender === 'female' ? 'Female' : 'Other'}
                      </small>
                    </td>
                    <td>
                      <div>
                        <FaEnvelope className="me-1 text-muted" size={12} />
                        <small>{item.email}</small>
                      </div>
                      <div>
                        <FaPhone className="me-1 text-muted" size={12} />
                        <small>{item.phone}</small>
                      </div>
                    </td>
                    <td>{item.education_level || 'N/A'}</td>
                    <td>{item.course || 'N/A'}</td>
                    <td>{item.scholarship_type || 'N/A'}</td>
                    <td>
                      <div className="status-dropdown">
                        <select
                          className="form-select form-select-sm"
                          value={item.status}
                          onChange={(e) => handleStatusChange(item.id, e.target.value)}
                          disabled={loadingId === item.id}
                          style={{ minWidth: '110px' }}
                        >
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>
                    </td>
                    <td>{formatDate(item.created_at)}</td>
                    <td className="text-center">
                      {/* VIEW */}
                      <button
                        className="st-action-btn st-view-btn me-1"
                        onClick={() =>
                          dispatch(
                            openModal({
                              type: "view-student",
                              title: "View Student",
                              data: item,
                            })
                          )
                        }
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION */}
        {showPagination && students.length > 0 && (
          <div className="d-flex flex-wrap align-items-center justify-content-between mt-4">
            <div className="text-muted small mb-2 mb-md-0">
              Showing {((currentPage - 1) * 20) + 1} to{' '}
              {Math.min(currentPage * 20, totalItems)} of {totalItems} entries
            </div>
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                // Handle page change
                console.log('Page changed:', page);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentRegistrations;